/**
 * @fileoverview The canonical stored form of a hand-typed name.
 *
 * Extracted verbatim from `backend/src/utils/sanitiser.js`, which is now gone.
 * It governs what actually lands in `MasterProduct.ProductName`,
 * `Category.CategoryName` and `CreditCustomer.CustomerName`, and the first two of
 * those are `UNIQUE`. Two implementations of it would be a slow-motion bug: the
 * cloud storing an accented product name where the till stores the folded,
 * uppercased one gives a single product two rows, and any later attempt to
 * reconcile them hits the unique constraint rather than merging.
 *
 * Not to be confused with `sanitisePrintable` in receiptLayout.js. That one
 * decides what a thermal printer can render and leaves case and spacing alone;
 * this one decides what is stored, and normalises aggressively -- accents
 * folded, punctuation regularised, uppercased -- so that two people typing the
 * same product name a week apart produce the same row.
 *
 * The uppercasing is not decoration. Shelf-edge labels and receipts are printed
 * in a fixed-width font at 48 columns, and a mixed-case catalogue reads as a
 * mistake next to the entries typed in capitals. All 25,404 names in dev01 are
 * already uppercase.
 *
 * Every character below is written as a `\u` escape rather than as the literal
 * character, so this file is pure ASCII. Combining marks are invisible in a
 * diff and the dashes and quote marks are indistinguishable from their ASCII
 * counterparts at a glance; a class corrupted by a copy-paste fails silently
 * here, by simply no longer folding something.
 */

/**
 * Normalises a name to the form the database stores.
 *
 * @param {string} input
 * @returns {string}
 * @throws {Error} if given anything other than a string -- callers must decide
 *   what a missing name means, and silently returning `''` would write one.
 */
function sanitiseString(input) {
  if (typeof input !== 'string') {
    throw new Error('Invalid input: must be a string');
  }

  return input
    .replace(/\s+/g, ' ') // collapse runs of whitespace to one space
    .normalize('NFKD') // decompose: e-acute becomes 'e' plus a combining mark
    .replace(/[\u0300-\u036f]/g, '') // drop the combining marks left behind
    .replace(/[\u2018\u2019]/g, "'") // curly quotes to a straight apostrophe
    .replace(/[\u2013\u2014]/g, '-') // en and em dash to hyphen
    .replace(/\u00d7/g, 'x') // multiplication sign
    .replace(/\u00b0/g, ' DEG ') // degree sign
    // Only U+00AE actually reaches this. NFKD above has already decomposed
    // U+2122 into the letters 'TM', so `Nike(TM)` is stored as `NIKETM` and the
    // second half of this class never matches. Kept as the till ships it: the
    // catalogue was written this way and "fixing" it would rename live rows.
    .replace(/[\u00ae\u2122]/g, '')
    .replace(/[\u00f8\u00d8]/g, 'O') // o-slash, which NFKD does not decompose
    .replace(/\u0142/g, 'l') // l-stroke, likewise
    .replace(/\u0141/g, 'L')
    .replace(/[^\x20-\x7e\u00a3]/g, '') // printable ASCII, plus the pound sign
    .replace(/\s+/g, ' ') // the removals above can leave gaps
    .trim()
    .toUpperCase();
}

module.exports = { sanitiseString };
